// src/components/common/SparkleButton.jsx
import React, { useEffect, useRef } from "react";

import sp1 from "../../assets/icon/sparkle.svg";
import sp2 from "../../assets/icon/sparkle1.svg";
import sp3 from "../../assets/icon/sparkle2.svg";
import sp4 from "../../assets/icon/sparkle3.svg";
import sp5 from "../../assets/icon/sparkle4.svg";

export default function SparkleButton({
  children,
  onClick,
  className = "",
  ariaLabel,
  type = "button",
}) {
  const btnRef = useRef(null);

  useEffect(() => {
    const btn = btnRef.current;
    if (!btn) return;

    // tâm nút
    const br = btn.getBoundingClientRect();
    const cx = br.left + br.width / 2;
    const cy = br.top + br.height / 2;

    // tính offset từ tâm nút -> vị trí hiện tại của từng sparkle
    const sparks = btn.querySelectorAll("[data-sparkle]");
    sparks.forEach((el) => {
      const r = el.getBoundingClientRect();
      const tx = r.left + r.width / 2 - cx;   // từ tâm nút đến sparkle
      const ty = r.top + r.height / 2 - cy;

      // set biến CSS: ban đầu dịch ngược về tâm; hover thì về 0
      el.style.setProperty("--fromX", `${-tx}px`);
      el.style.setProperty("--fromY", `${-ty}px`);
    });
  }, []);

  return (
    <button
      ref={btnRef}
      type={type}
      onClick={onClick}
      aria-label={ariaLabel ?? (typeof children === "string" ? children : "CTA")}
      className={`
        group relative inline-flex items-center justify-center
        rounded-full border border-brandBlue-700 bg-brandBlue-500 text-white
        transition-all duration-300 ease-out transform-gpu
        hover:ring-1 hover:ring-cyan-300/60
        hover:shadow-[0_2px_2px_#8BBAF3,0_0_2px_#8BBAF3]
        px-4 py-2 ${className}
      `}
    >
      {/* Glass highlight trong nút */}
      <span
        className="pointer-events-none absolute inset-0 rounded-full
                   bg-gradient-to-b from-white/25 to-transparent
                   opacity-40 transition-opacity duration-300 mix-blend-screen"
        aria-hidden="true"
      />

      {/* Glow phía sau nút */}
      <span
        className="pointer-events-none absolute -inset-2 -z-10 rounded-full
                   bg-cyan-400/0 blur-0 transition-all duration-500 ease-out
                   group-hover:blur-xl"
        aria-hidden="true"
      />

      {/* Nội dung */}
      <span className="relative z-10 font-normal">{children}</span>

      {/* ====== SPARKLES (bung từ giữa) ====== */}
      {/* Giữ NGUYÊN toạ độ của bạn, chỉ thêm data-sparkle và class transform */}
      {/* 1 */}
      <span className="pointer-events-none absolute right-[2%] bottom-[90%]" data-sparkle>
        <img
          src={sp1}
          alt=""
          className="
            h-3 w-3 opacity-0
            [--tx:var(--fromX)] [--ty:var(--fromY)] [--scale:0]
            group-hover:[--tx:0px] group-hover:[--ty:0px] group-hover:[--scale:1]
            [transform:translate3d(var(--tx),var(--ty),0)_scale(var(--scale))]
            transition-transform transition-opacity duration-500 ease-out
            drop-shadow-[0_0_10px_rgba(0,166,255,0.8)]
            group-hover:opacity-100
          "
        />
      </span>
      {/* 2 */}
      <span className="pointer-events-none absolute right-[88%] top-[-25%]" data-sparkle>
        <img
          src={sp2}
          alt=""
          className="
            h-3.5 w-3.5 opacity-0
            [--tx:var(--fromX)] [--ty:var(--fromY)] [--scale:0]
            group-hover:[--tx:0px] group-hover:[--ty:0px] group-hover:[--scale:1]
            [transform:translate3d(var(--tx),var(--ty),0)_scale(var(--scale))]
            transition-transform transition-opacity duration-500 ease-out delay-75
            drop-shadow-[0_0_8px_rgba(0,166,255,0.75)]
            group-hover:opacity-100
          "
        />
      </span>
      {/* 3 */}
      <span className="pointer-events-none absolute -right-[6%] top-[90%]" data-sparkle>
        <img
          src={sp3}
          alt=""
          className="
            h-5 w-5 mobile:h-3 mobile:w-3 opacity-0
            [--tx:var(--fromX)] [--ty:var(--fromY)] [--scale:0]
            group-hover:[--tx:0px] group-hover:[--ty:0px] group-hover:[--scale:1]
            [transform:translate3d(var(--tx),var(--ty),0)_scale(var(--scale))]
            transition-transform transition-opacity duration-500 ease-out delay-100
            drop-shadow-[0_0_12px_rgba(0,166,255,0.9)]
            group-hover:opacity-100
          "
        />
      </span>
      {/* 4 */}
      <span className="pointer-events-none absolute left-[1%] -bottom-[30%] mobile:-bottom-[30%]" data-sparkle>
        <img
          src={sp4}
          alt=""
          className="
            h-6 w-6 mobile:h-4 mobile:w-4 opacity-0
            [--tx:var(--fromX)] [--ty:var(--fromY)] [--scale:0]
            group-hover:[--tx:0px] group-hover:[--ty:0px] group-hover:[--scale:1]
            [transform:translate3d(var(--tx),var(--ty),0)_scale(var(--scale))]
            transition-transform transition-opacity duration-500 ease-out delay-150
            drop-shadow-[0_0_8px_rgba(0,166,255,0.75)]
            group-hover:opacity-100
          "
        />
      </span>
      {/* 5 */}
      <span className="pointer-events-none absolute -right-[6%] top-[70%]" data-sparkle>
        <img
          src={sp5}
          alt=""
          className="
            h-2.7 w-2.7 mobile:h-2 mobile:w-2 opacity-0
            [--tx:var(--fromX)] [--ty:var(--fromY)] [--scale:0]
            group-hover:[--tx:0px] group-hover:[--ty:0px] group-hover:[--scale:1]
            [transform:translate3d(var(--tx),var(--ty),0)_scale(var(--scale))]
            transition-transform transition-opacity duration-500 ease-out delay-200
            drop-shadow-[0_0_10px_rgba(0,166,255,0.85)]
            group-hover:opacity-100
          "
        />
      </span>
    </button>
  );
}
